/**
 * Actual P&L (Sales) visibility gate — display filter only.
 *
 * Show a lead only when:
 * 1) It is in the salesperson Project Ongoing path
 *    (Assignedperson.salespersonstatus is "project ongoing" or later "project complete"), AND
 * 2) Managers have submitted from their Ongoing basket
 *    (every Assignedperson.isProjectComplete === true — same as "Submit Project").
 *
 * Does not show Project Approved / not-yet-submitted Ongoing leads.
 * Does not change expense calculations.
 */

const defaultGetRowKey = (row) =>
  row?.quotationId != null && String(row.quotationId).trim() !== ""
    ? String(row.quotationId).trim()
    : String(row?.ProjectName ?? "").trim();

const normalizeStatus = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const isTruthyComplete = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value ?? "")
    .trim()
    .toLowerCase() === "true";

const isInSalesOngoingPath = (person) => {
  const status = normalizeStatus(person?.salespersonstatus);
  return status === "project ongoing" || status === "project complete";
};

const isManagerSubmitted = (person) => isTruthyComplete(person?.isProjectComplete);

const matchTrackingProjectsForLead = (row, projects, getRowKey) => {
  const rowKey = String(getRowKey(row) ?? "").trim();
  if (!rowKey || !Array.isArray(projects) || projects.length === 0) return [];

  const rowProjectName = String(row?.ProjectName ?? "").trim();

  return projects.filter((project) => {
    if (project?.isProjecttracking !== true) return false;

    const projectKey =
      project?.quotationId != null && String(project.quotationId).trim() !== ""
        ? String(project.quotationId).trim()
        : String(project?.ProjectName ?? "").trim();

    if (projectKey && projectKey === rowKey) return true;
    if (
      rowProjectName &&
      String(project?.ProjectName ?? "").trim() === rowProjectName
    ) {
      return true;
    }
    return false;
  });
};

/**
 * @param {{ row: object, projects?: array, getRowKey?: (row) => string }} params
 * @returns {boolean}
 */
export function shouldShowLeadOnActualPl({
  row,
  projects = [],
  getRowKey = defaultGetRowKey,
}) {
  const trackingProjects = matchTrackingProjectsForLead(row, projects, getRowKey);
  if (!trackingProjects.length) return false;

  // Every tracking project for this lead must be on the sales Ongoing path AND
  // fully submitted by managers from Ongoing (isProjectComplete on every assignee).
  return trackingProjects.every((project) => {
    const people = project?.Assignedperson;
    if (!Array.isArray(people) || people.length === 0) return false;

    const inSalesOngoingOrComplete = people.some(isInSalesOngoingPath);
    if (!inSalesOngoingOrComplete) return false;

    return people.every(isManagerSubmitted);
  });
}

/** @deprecated Prefer shouldShowLeadOnActualPl — kept for any existing imports. */
export function hasSubmittedProjectFromOngoing(params) {
  return shouldShowLeadOnActualPl(params);
}
